import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export type QuantityInputType = 'arrows_manual' | 'dropdown';
export type SubscriptionSelectorType = 'dropdown' | 'upgrade_button';
export type CartItemImageFit = 'cover' | 'contain';
export type CartItemQuantityPosition = 'bottom' | 'inline';

// The one definition of the cart line photo box's defaults — the editor form, the preview and the
// storefront fallback all read these. backgroundColor must match SURFACE_HIGH in the storefront's
// lib/surfaceColors.ts (not the admin's --surface-container-high, which is a different grey).
export const CART_ITEM_IMAGE_DEFAULTS = {
  fit: 'cover' as CartItemImageFit,
  width: 104,
  height: 130,
  backgroundColor: '#f6f6f7',
};

// Cart line photo box. The defaults are the size every cart already renders, so an untouched widget is
// unchanged; the bounds keep a hand-typed value from crowding out the title/quantity column.
export const CART_ITEM_IMAGE_BOUNDS = {
  width: { min: 64, max: 180 },
  height: { min: 64, max: 260 },
};

function quantityRangeValidator(group: AbstractControl): ValidationErrors | null {
  const min = group.get('min')?.value;
  const max = group.get('max')?.value;
  if (typeof min === 'number' && typeof max === 'number' && min > max) {
    return { minGreaterThanMax: true };
  }
  return null;
}

export interface CartItemsFormShape {
  emptyCartText: FormControl<string>;
  emptyCartTextColor: FormControl<string>;
  quantityInput: FormGroup<{
    min: FormControl<number>;
    max: FormControl<number>;
    inputType: FormControl<QuantityInputType>;
    minQuantityMessage: FormControl<string>;
    maxQuantityMessage: FormControl<string>;
    position: FormControl<CartItemQuantityPosition>;
  }>;
  subscription: FormGroup<{
    enabled: FormControl<boolean>;
    selectorType: FormControl<SubscriptionSelectorType>;
    label: FormControl<string>;
    upgradingLabel: FormControl<string>;
    // Design fields are nullable: null means "inherit from global"; a value means an explicit
    // per-widget override (storefront resolves `widget.design.X ?? global.buttonDesign.X`).
    design: FormGroup<{
      backgroundColor: FormControl<string | null>;
      textColor: FormControl<string | null>;
      hoverColor: FormControl<string | null>;
      iconColor: FormControl<string | null>;
      cornerRadius: FormControl<number | null>;
    }>;
  }>;
  changeVariant: FormControl<boolean>;
  image: FormGroup<{
    fit: FormControl<CartItemImageFit>;
    width: FormControl<number>;
    height: FormControl<number>;
    backgroundColor: FormControl<string>;
  }>;
  customCode: FormGroup<CustomCodeFormShape>;
}

const DEFAULTS = {
  emptyCartText: '<strong>Your cart is empty!</strong><br>Add your favorite items to your cart.',
  // The colour the display text has always rendered in, so adding this control changes nothing.
  emptyCartTextColor: '#666666',
  quantityInput: {
    min: 0,
    max: 10,
    inputType: 'arrows_manual' as QuantityInputType,
    minQuantityMessage: 'Min Qty of {min}',
    maxQuantityMessage: 'Max Qty of {max}',
    position: 'bottom' as CartItemQuantityPosition,
  },
  subscription: {
    enabled: false,
    selectorType: 'upgrade_button' as SubscriptionSelectorType,
    label: 'Subscribe & Save {subscription_discount}%',
    upgradingLabel: 'Upgrading...',
  },
  changeVariant: false,
  image: CART_ITEM_IMAGE_DEFAULTS,
};

export const CART_ITEMS_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{emptyCartText}}',
    type: 'String',
    description: 'Configured empty-cart message from the admin — render with triple braces to preserve formatting',
  },
  { reference: '{{emptyCartTextColor}}', type: 'String', description: "Hex of the display text's colour" },
  {
    reference: '{{items}}',
    type: 'Array',
    description: 'The cart line items, in cart order. Loop with {{#each items}}',
  },
  { reference: '{{itemCount}}', type: 'Number', description: 'Total units in the cart (sum of every line quantity)' },
  {
    reference: '{{totalPrice}}',
    type: 'String',
    description: 'Cart total after discounts, formatted in the shop currency',
  },
  {
    reference: '{{originalTotalPrice}}',
    type: 'String',
    description: 'Cart total before discounts, formatted. Show it struck through next to {{totalPrice}}',
  },
  {
    reference: '{{totalPriceCents}}',
    type: 'Number',
    description: 'Cart total after discounts in cents, for your own math',
  },
  {
    reference: '{{hasDiscount}}',
    type: 'Boolean',
    description: 'True when the cart total is discounted. Use with {{#if hasDiscount}}',
  },
  {
    reference: '{{discountAmount}}',
    type: 'String',
    description: 'Total cart savings, formatted in the shop currency',
  },
  {
    reference: '{{items[i].key}}',
    type: 'String',
    description: 'Shopify line key. The remove and quantity actions need it on data-pdq-line-key',
  },
  {
    reference: '{{items[i].productTitle}}',
    type: 'String',
    description: 'Shopper input: product title as it appears on the line. Always render with double braces',
  },
  {
    reference: '{{items[i].variantTitle}}',
    type: 'String',
    description:
      'Shopper input: selected variant title. Empty for single-variant products, so guard with {{#if variantTitle}}',
  },
  { reference: '{{items[i].handle}}', type: 'String', description: 'Product handle' },
  { reference: '{{items[i].url}}', type: 'String', description: 'Product page URL for this line' },
  {
    reference: '{{items[i].imageUrl}}',
    type: 'String',
    description: 'Line image URL (empty when the product has no image, so guard with {{#if imageUrl}})',
  },
  { reference: '{{items[i].quantity}}', type: 'Number', description: 'Units of this line currently in the cart' },
  {
    reference: '{{items[i].price}}',
    type: 'String',
    description:
      'Line total after discounts (unit price x quantity), formatted in the shop currency. There is no unit-price variable',
  },
  {
    reference: '{{items[i].compareAtPrice}}',
    type: 'String',
    description:
      'Line total before discounts, formatted. For a bundle this is what its contents cost bought separately. Empty when the line is not discounted',
  },
  {
    reference: '{{items[i].savings}}',
    type: 'String',
    description:
      'What this line saves off its compare-at price, formatted. For a bundle, what its contents would cost separately minus its own price. Empty when the line is not discounted',
  },
  {
    reference: '{{items[i].hasDiscount}}',
    type: 'Boolean',
    description:
      'True when this line is discounted, or when a bundle costs less than its contents. Bundle savings are not a Shopify discount, so the cart-level {{hasDiscount}} stays false',
  },
  {
    reference: '{{items[i].priceCents}}',
    type: 'Number',
    description: 'Line total after discounts in cents, for your own math',
  },
  {
    reference: '{{items[i].compareAtPriceCents}}',
    type: 'Number',
    description: 'Line total before discounts in cents. Empty when the line is not discounted',
  },
  {
    reference: '{{items[i].savingsCents}}',
    type: 'Number',
    description: 'What this line saves off its compare-at price, in cents. Empty when the line is not discounted',
  },
  {
    reference: '{{items[i].components}}',
    type: 'Array',
    description:
      'What is inside a Shopify bundle line, in bundle order. Loop with {{#each components}}. Empty for an ordinary product',
  },
  {
    reference: '{{items[i].componentCount}}',
    type: 'Number',
    description: 'Total units inside a bundle line (a Pick 6 reads 6). Zero for an ordinary product',
  },
  {
    reference: '{{items[i].components[j].productTitle}}',
    type: 'String',
    description: 'Product title of one item inside the bundle. Always render with double braces',
  },
  {
    reference: '{{items[i].components[j].variantTitle}}',
    type: 'String',
    description:
      'Variant title of one item inside the bundle; often "Default Title", so guard with {{#if variantTitle}}',
  },
  {
    reference: '{{items[i].components[j].quantity}}',
    type: 'Number',
    description: 'How many of this item the bundle contains',
  },
  {
    reference: '{{items[i].components[j].price}}',
    type: 'String',
    description: "One unit's own price, formatted — what it would cost bought separately",
  },
  {
    reference: '{{items[i].components[j].priceCents}}',
    type: 'Number',
    description: "One unit's own price in cents, for your own math",
  },
  {
    reference: '{{items[i].sellingPlanName}}',
    type: 'String',
    description: 'Subscription plan name on this line; empty for one-time purchases',
  },
  {
    reference: '{{items[i].sellingPlanId}}',
    type: 'Number',
    description: 'Selling plan id on this line; empty for one-time purchases',
  },
  {
    reference: '{{items[i].sellingPlans}}',
    type: 'Array',
    description:
      'Subscription plans Shopify offers this line. Loop with {{#each sellingPlans}} to build a plan picker. Empty unless the Subscription control is on',
  },
  {
    reference: '{{items[i].sellingPlans[j].id}}',
    type: 'Number',
    description: 'Plan id — the value a data-pdq-action="set-selling-plan" select submits',
  },
  {
    reference: '{{items[i].sellingPlans[j].name}}',
    type: 'String',
    description:
      'Plan name, as "{group} / {plan}", or just "{plan}" when both names match. Always render with double braces',
  },
  {
    reference: '{{items[i].sellingPlans[j].isSelected}}',
    type: 'Boolean',
    description: 'True for the plan this line is on now — use it to mark the selected option',
  },
  {
    reference: '{{items[i].sellingPlans[j].discountPercent}}',
    type: 'Number',
    description: 'Percent off for this plan; empty when the plan takes a fixed amount off instead',
  },
  {
    reference: '{{items[i].variantId}}',
    type: 'Number',
    description: 'Variant id this line is on now',
  },
  {
    reference: '{{items[i].variants}}',
    type: 'Array',
    description:
      'In-stock variants of this line. Loop with {{#each variants}} to build a variant picker. Empty unless the Change Variant control is on',
  },
  {
    reference: '{{items[i].variants[j].id}}',
    type: 'Number',
    description: 'Variant id — the value a data-pdq-action="set-variant" select submits',
  },
  {
    reference: '{{items[i].variants[j].title}}',
    type: 'String',
    description: 'Variant title. Always render with double braces',
  },
  {
    reference: '{{items[i].variants[j].isSelected}}',
    type: 'Boolean',
    description: 'True for the variant this line is on now — use it to mark the selected option',
  },
  {
    reference: '{{items[i].variants[j].price}}',
    type: 'String',
    description: "This variant's price, formatted in the shop currency",
  },
  {
    reference: '{{items[i].variants[j].priceCents}}',
    type: 'Number',
    description: "This variant's price in cents, for your own math",
  },
  {
    reference: '{{items[i].variantOptions}}',
    type: 'Array',
    description:
      'One group per product option (Dose, Flavor) for building two dropdowns instead of one combined list. Loop with {{#each variantOptions}}. Empty unless the Change Variant control is on',
  },
  {
    reference: '{{items[i].variantOptions[j].name}}',
    type: 'String',
    description: 'Option name — pass it as data-pdq-option-name on a data-pdq-action="set-variant-option" select',
  },
  {
    reference: '{{items[i].variantOptions[j].selectedValue}}',
    type: 'String',
    description: 'The value this line is on for that option',
  },
  {
    reference: '{{items[i].variantOptions[j].values}}',
    type: 'Array',
    description:
      'Choices for that option, already narrowed by the options before it, so Flavor lists only what the chosen Dose has',
  },
  {
    reference: '{{items[i].variantOptions[j].values[k].value}}',
    type: 'String',
    description: 'One choice — the value the set-variant-option select submits',
  },
  {
    reference: '{{items[i].variantOptions[j].values[k].isSelected}}',
    type: 'Boolean',
    description: 'True for the choice this line is on — use it to mark the selected option',
  },
  {
    reference: '{{items[i].discountLabels}}',
    type: 'Array',
    description:
      'Discounts applied to this line. Loop with {{#each discountLabels}} to show them the way the default row does',
  },
  {
    reference: '{{items[i].discountLabels[j].title}}',
    type: 'String',
    description:
      'Shopper input: the discount name, which is the code a shopper typed. Always render with double braces',
  },
  {
    reference: '{{items[i].discountLabels[j].amount}}',
    type: 'String',
    description: 'What this discount takes off the line, formatted and negative',
  },
  {
    reference: '{{items[i].discountLabels[j].amountCents}}',
    type: 'Number',
    description: 'What this discount takes off the line, in cents and negative',
  },
  {
    reference: '{{items[i].discountLabels[j].color}}',
    type: 'String',
    description: 'Hex colour the default row tints this label with; drop it straight into a style attribute',
  },
  {
    reference: '{{items[i].isGift}}',
    type: 'Boolean',
    description: 'True for a goal reward line. Give it a Remove button only — a stepper or picker on a gift is ignored',
  },
  {
    reference: '{{items[i].properties}}',
    type: 'Array',
    description:
      'Shopper input: line item properties (gift message, engraving, custom fields). Loop with {{#each properties}}',
  },
  {
    reference: '{{items[i].properties[j].name}}',
    type: 'String',
    description: 'Shopper input: property label. Always render with double braces',
  },
  {
    reference: '{{items[i].properties[j].value}}',
    type: 'String',
    description: 'Shopper input: property value. Always render with double braces',
  },
];

export const CART_ITEMS_STARTER_TEMPLATE = `<div class="cart-items">
  <!-- Visible when the cart has items. Every line is yours to lay out. The data-pdq-action
       attributes below wire your buttons to the cart, and each one needs the line's key.
       Want the default PDQ line item UI instead? Replace this whole branch with
       <div data-pdq-slot="cart-items-list"></div> and PDQ renders the items for you. -->
  <div data-pdq-show-if="has-items">
    <div class="cart-items__list">
      {{#each items}}
        <div class="cart-item">
          {{#if imageUrl}}
            <a class="cart-item__image-link" href="{{url}}" tabindex="-1" aria-hidden="true">
              <img class="cart-item__image" src="{{imageUrl}}" alt="{{productTitle}}" />
            </a>
          {{/if}}

          <div class="cart-item__details">
            <a class="cart-item__title" href="{{url}}">{{productTitle}}</a>

            {{#if variantTitle}}
              <div class="cart-item__variant">{{variantTitle}}</div>
            {{/if}}

            {{#if sellingPlanName}}
              <div class="cart-item__selling-plan">{{sellingPlanName}}</div>
            {{/if}}

            {{#each properties}}
              <div class="cart-item__property">{{name}}: {{value}}</div>
            {{/each}}

            <div class="cart-item__quantity">
              <button
                type="button"
                class="cart-item__quantity-button"
                aria-label="Decrease quantity"
                data-pdq-action="decrease-item"
                data-pdq-line-key="{{key}}"
              >&minus;</button>
              <input
                type="number"
                class="cart-item__quantity-input"
                value="{{quantity}}"
                aria-label="Quantity for {{productTitle}}"
                data-pdq-action="set-quantity"
                data-pdq-line-key="{{key}}"
              />
              <button
                type="button"
                class="cart-item__quantity-button"
                aria-label="Increase quantity"
                data-pdq-action="increase-item"
                data-pdq-line-key="{{key}}"
              >+</button>
            </div>
          </div>

          <div class="cart-item__pricing">
            <span class="cart-item__price">{{price}}</span>
            {{#if hasDiscount}}
              <s class="cart-item__compare-at-price">{{compareAtPrice}}</s>
            {{/if}}
            <button
              type="button"
              class="cart-item__remove"
              data-pdq-action="remove-item"
              data-pdq-line-key="{{key}}"
            >Remove</button>
          </div>
        </div>
      {{/each}}
    </div>

    <div class="cart-items__summary">
      <span class="cart-items__summary-label">Subtotal ({{itemCount}})</span>
      <span class="cart-items__summary-value">
        {{#if hasDiscount}}
          <s class="cart-items__original-total">{{originalTotalPrice}}</s>
        {{/if}}
        {{totalPrice}}
      </span>
    </div>

    {{#if hasDiscount}}
      <div class="cart-items__savings">You save {{discountAmount}}</div>
    {{/if}}
  </div>

  <!-- Visible when the cart is empty; customize this layout freely. -->
  <div data-pdq-show-if="empty" style="padding: 40px 20px; color: {{emptyCartTextColor}};">
    {{{emptyCartText}}}
  </div>
</div>`;

export function createCartItemsFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<CartItemsFormShape> {
  const formGroup = new FormGroup<CartItemsFormShape>({
    emptyCartText: new FormControl(DEFAULTS.emptyCartText, { nonNullable: true }),
    emptyCartTextColor: new FormControl(DEFAULTS.emptyCartTextColor, { nonNullable: true }),
    quantityInput: new FormGroup(
      {
        min: new FormControl(DEFAULTS.quantityInput.min, { nonNullable: true }),
        max: new FormControl(DEFAULTS.quantityInput.max, { nonNullable: true }),
        inputType: new FormControl(DEFAULTS.quantityInput.inputType, { nonNullable: true }),
        minQuantityMessage: new FormControl(DEFAULTS.quantityInput.minQuantityMessage, { nonNullable: true }),
        maxQuantityMessage: new FormControl(DEFAULTS.quantityInput.maxQuantityMessage, { nonNullable: true }),
        position: new FormControl(DEFAULTS.quantityInput.position, { nonNullable: true }),
      },
      { validators: quantityRangeValidator },
    ),
    subscription: new FormGroup({
      enabled: new FormControl(DEFAULTS.subscription.enabled, { nonNullable: true }),
      selectorType: new FormControl(DEFAULTS.subscription.selectorType, { nonNullable: true }),
      label: new FormControl(DEFAULTS.subscription.label, { nonNullable: true }),
      upgradingLabel: new FormControl(DEFAULTS.subscription.upgradingLabel, { nonNullable: true }),
      design: new FormGroup({
        backgroundColor: new FormControl<string | null>(null),
        textColor: new FormControl<string | null>(null),
        hoverColor: new FormControl<string | null>(null),
        iconColor: new FormControl<string | null>(null),
        cornerRadius: new FormControl<number | null>(null),
      }),
    }),
    changeVariant: new FormControl(DEFAULTS.changeVariant, { nonNullable: true }),
    image: new FormGroup({
      fit: new FormControl(DEFAULTS.image.fit, { nonNullable: true }),
      width: new FormControl(DEFAULTS.image.width, {
        nonNullable: true,
        validators: [
          Validators.min(CART_ITEM_IMAGE_BOUNDS.width.min),
          Validators.max(CART_ITEM_IMAGE_BOUNDS.width.max),
        ],
      }),
      height: new FormControl(DEFAULTS.image.height, {
        nonNullable: true,
        validators: [
          Validators.min(CART_ITEM_IMAGE_BOUNDS.height.min),
          Validators.max(CART_ITEM_IMAGE_BOUNDS.height.max),
        ],
      }),
      backgroundColor: new FormControl(DEFAULTS.image.backgroundColor, { nonNullable: true }),
    }),
    customCode: createCustomCodeFormGroup(undefined, CART_ITEMS_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
  }

  return formGroup;
}
